import dayjs from 'dayjs'
import { uniqBy } from 'lodash'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { api } from '../../../utils/api'
import { savvyGoldClient } from '../../../utils/api/savvy-gold-client'
import {
  GetGoldProductBody,
  GetProductsResponseType,
  GetSellPriceResponse,
  GoldProductType,
} from './sell-typed'

export const BASE_SELL_URL = `internal/sell`
export const GET_SELL_PRICE_URL = `${BASE_SELL_URL}/sell-prices`
export const GET_GOLD_PRODUCTS_URL = `${BASE_SELL_URL}/products`

export const useGetSellPrice = (options?: UseQueryOptions<GetSellPriceResponse>) => {
  return useQuery<GetSellPriceResponse>(
    [GET_SELL_PRICE_URL],
    async () => {
      const { data: res } = await savvyGoldClient.get(GET_SELL_PRICE_URL)
      const data = res.data as GetSellPriceResponse
      return { ...data, updatedAt: dayjs() }
    },
    {
      cacheTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
      ...options,
    },
  )
}

export const customGoldProduct = (product: GoldProductType) => {
  const { name, goldPattern, brand, ...restProduct } = product
  return {
    ...restProduct,
    brand: brand.toLocaleLowerCase(),
    goldPattern: goldPattern,
    pattern: goldPattern === 'ลายโลโก้ออสสิริส' ? 'ลายมาตรฐาน' : goldPattern,
    name: name.replace('บาท', 'บาททอง'),
  } as const
}

export const useGetGoldProducts = (
  params?: GetGoldProductBody,
  options?: UseQueryOptions<GoldProductType[], Error, GoldProductType[]>,
) => {
  const queryClient = useQueryClient()
  return useQuery(
    [GET_GOLD_PRODUCTS_URL, params],
    async () => {
      const { data: res } = await savvyGoldClient.get(GET_GOLD_PRODUCTS_URL, {
        params,
      })
      const data = res.data as GetProductsResponseType
      return uniqBy(data.products, e => e.id).map(customGoldProduct)
    },
    {
      onSuccess: async products => {
        const tmp = products.map(async product => {
          const { id: productId } = product
          queryClient.setQueryData([GET_GOLD_PRODUCTS_URL, productId], product, {
            updatedAt: dayjs()
              .add(7, 'minute')
              .unix(),
          })
        })
        await Promise.all(tmp)
      },
      staleTime: 0,
      ...options,
    },
  )
}

export const useGetGoldProductDetail = () => {
  return useMutation(async (params?: GetGoldProductBody) => {
    const { data: res } = await savvyGoldClient.get(GET_GOLD_PRODUCTS_URL, {
      params,
    })

    const data = res.data as GetProductsResponseType
    const products = data?.products
    return products ? products.map(product => customGoldProduct(product)) : undefined
  }, {})
}
